import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiArrowLeft,
  FiDownload,
  FiFileText,
  FiPaperclip,
  FiCamera,
  FiRotateCcw,
  FiShoppingBag,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiX,
  FiSend,
} from 'react-icons/fi';
import api, { downloadAuthenticatedFile, openAuthenticatedFile } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import IconButton from '../components/IconButton';
import LoadingButton from '../components/LoadingButton';
import { StatusBadge, formatCents as money } from '../components/shared/Ui';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { appAlert, appConfirm } from '../utils/appDialog';
import { companyOptionLabel, resolveDocumentCompanyId } from '../utils/legalCompany';
import { formatLocalDateTime } from '../utils/formatTimestamp';
import './Services.css';
import '../components/erp/Erp.css';
import './Requisitions.css';

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/tiff,.pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff';

function kindFromPath(pathname) {
  if (pathname.includes('/bills')) return 'bill';
  if (pathname.includes('/quotations')) return 'quotation';
  return 'po';
}

function centsToAmount(cents) {
  if (cents == null || cents === '') return '';
  return (Number(cents) / 100).toFixed(2);
}

function emptyLine() {
  return { description: '', quantity: 1, unit_price: '' };
}

function linesFromApi(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return [emptyLine(), emptyLine()];
  return lines.map((line) => ({
    description: line.description || '',
    quantity: line.quantity != null && line.quantity !== '' ? line.quantity : 1,
    unit_price:
      line.unit_price != null && line.unit_price !== ''
        ? String(line.unit_price)
        : centsToAmount(line.unit_price_cents || 0),
  }));
}

function linesFromOcr(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  return lines.map((line) => ({
    description: line.description || '',
    quantity: line.quantity != null && line.quantity !== '' ? line.quantity : 1,
    unit_price: centsToAmount(line.unit_price_cents != null ? line.unit_price_cents : line.amount_cents),
  }));
}

function lineHasContent(line) {
  return Boolean(String(line?.description || '').trim()) || Number(line?.unit_price) > 0;
}

function toPayloadLines(lines, fallbackDescription) {
  const prepared = (Array.isArray(lines) ? lines : [])
    .filter(lineHasContent)
    .map((line) => {
      const quantity = Number(line.quantity) || 1;
      const unitPriceCents = Math.round(Number(line.unit_price || 0) * 100);
      return {
        description: String(line.description || '').trim() || fallbackDescription,
        quantity,
        unit_price_cents: unitPriceCents,
        amount_cents: Math.round(quantity * unitPriceCents),
      };
    });
  if (prepared.length) return prepared;
  return [{ description: fallbackDescription, quantity: 1, unit_price_cents: 0, amount_cents: 0 }];
}

function matchVendorId(vendors, name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return '';
  const exact = vendors.find((v) => String(v.name || '').toLowerCase() === n);
  if (exact) return exact.id;
  const partial = vendors.find((v) => {
    const vn = String(v.name || '').toLowerCase();
    return vn && (vn.includes(n) || n.includes(vn));
  });
  return partial?.id || '';
}

function LinkedVendorPoDocuments({ detail, canQuote, canBill }) {
  if (!detail?.id) return null;
  const quotes = detail.linked_quotations || [];
  const bills = detail.linked_bills || [];
  const newQuoteTo = `/buying/quotations/new?vendor_id=${encodeURIComponent(detail.vendor_id || '')}&vendor_po_id=${encodeURIComponent(detail.id)}`;
  const newBillTo = `/buying/bills/new?vendor_id=${encodeURIComponent(detail.vendor_id || '')}&vendor_po_id=${encodeURIComponent(detail.id)}`;
  return (
    <div className="req-section">
      <div className="svc-form-section-head">
        <h3>Linked quotations</h3>
        {canQuote ? (
          <Link className="btn btn-secondary btn-sm" to={newQuoteTo}>
            <FiPlus aria-hidden /> New quotation
          </Link>
        ) : null}
      </div>
      {quotes.length ? (
        <ul className="erp-linked-docs">
          {quotes.map((q) => (
            <li key={q.id}>
              <Link to={`/buying/quotations/${q.id}`}>
                <span className="erp-code">{q.quotation_number || q.id}</span>
              </Link>
              <StatusBadge status={q.status} />
              <span className="erp-num">{money(q.total_cents, q.currency || detail.currency)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="form-hint">No quotations linked to this Vendor PO yet.</p>
      )}
      <div className="svc-form-section-head">
        <h3>Linked bills</h3>
        {canBill ? (
          <Link className="btn btn-secondary btn-sm" to={newBillTo}>
            <FiPlus aria-hidden /> New bill
          </Link>
        ) : null}
      </div>
      {bills.length ? (
        <ul className="erp-linked-docs">
          {bills.map((b) => (
            <li key={b.id}>
              <Link to={`/buying/bills/${b.id}`}>
                <span className="erp-code">{b.bill_number || b.id}</span>
              </Link>
              <StatusBadge status={b.status} />
              <span className="erp-num">{money(b.total_cents, b.currency || detail.currency)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="form-hint">No bills linked to this Vendor PO yet.</p>
      )}
    </div>
  );
}

function buyingStageLabel(status, stages) {
  const match = (stages || []).find((s) => s.stage_key === status);
  return match?.label || status;
}

function buyingStageByKey(stages, status) {
  return (stages || []).find((s) => s.stage_key === status) || null;
}

function isBuyingInitialStatus(status, stages) {
  const st = buyingStageByKey(stages, status);
  if (st) return Boolean(st.is_initial);
  return !status || status === 'draft';
}

function isBuyingRejectedStatus(status, stages) {
  const st = buyingStageByKey(stages, status);
  if (st) return Boolean(st.is_rejected);
  return status === 'rejected';
}

function isBuyingCompletedStatus(status, stages) {
  const st = buyingStageByKey(stages, status);
  if (st) return Boolean(st.is_completed);
  return status === 'approved';
}

function BuyingWorkflowPanel({
  detail,
  kind,
  workflowStages,
  canManage,
  canActOnStage,
  comment,
  setComment,
  transitionMutation,
}) {
  if (!detail) return null;
  const track = (workflowStages || [])
    .filter((s) => s.is_active !== 0 && !s.is_rejected)
    .map((s) => s.stage_key);
  const stages = track.length ? track : ['draft', 'review', 'approval', 'vendor_approval', 'approved'];
  const activeStageIdx = stages.indexOf(detail.status);
  const isDraftStatus = isBuyingInitialStatus(detail.status, workflowStages);
  const isRejectedStatus = isBuyingRejectedStatus(detail.status, workflowStages);
  const isCompletedStatus = isBuyingCompletedStatus(detail.status, workflowStages);
  const isAccounting = detail.status === 'posted' || detail.status === 'paid';
  const kindLabel = kind === 'bill' ? 'bill' : kind === 'quotation' ? 'quotation' : 'Vendor PO';

  return (
    <>
      <ol className="req-stage-track">
        {stages.map((s, idx) => (
          <li
            key={s}
            className={`req-stage${detail.status === s ? ' is-current' : ''}${
              activeStageIdx > idx || isCompletedStatus || isAccounting ? ' is-done' : ''
            }${isRejectedStatus && idx > 0 ? ' is-rejected' : ''}`}
          >
            {buyingStageLabel(s, workflowStages)}
          </li>
        ))}
      </ol>
      {(detail.vendor_signatory_name || detail.company_signatory_name) && (
        <div className="req-section">
          <h3>Parties</h3>
          <dl className="req-dl">
            <div>
              <dt>Vendor signatory</dt>
              <dd>
                {detail.vendor_signatory_name || '—'}
                {detail.vendor_signatory_title ? ` · ${detail.vendor_signatory_title}` : ''}
                {detail.vendor_signatory_email ? ` · ${detail.vendor_signatory_email}` : ''}
              </dd>
            </div>
            <div>
              <dt>Company signatory</dt>
              <dd>
                {detail.company_signatory_name || '—'}
                {detail.company_signatory_title ? ` · ${detail.company_signatory_title}` : ''}
              </dd>
            </div>
          </dl>
        </div>
      )}
      {(detail.stage_assignees || []).length > 0 && (
        <p className="svc-panel-meta">
          Assignees (AND gate — all must approve):{' '}
          {detail.stage_assignees
            .map((a) => {
              const approved = (detail.stage_approvals || []).some(
                (x) => Number(x.user_id) === Number(a.id)
              );
              return `${a.first_name} ${a.last_name}${a.from_vendor ? ' (vendor)' : ''}${
                approved ? ' ✓' : ''
              }`;
            })
            .join(', ')}
          {(detail.pending_approvers || []).length > 0
            ? ` · waiting on ${detail.pending_approvers.length}`
            : ''}
        </p>
      )}
      {!isAccounting && (
        <div className="req-workflow-actions">
          <textarea
            rows={2}
            placeholder="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="req-workflow-buttons">
            {canManage && (isDraftStatus || isRejectedStatus) && (
              <LoadingButton
                type="button"
                className="btn btn-primary btn-sm"
                icon={FiSend}
                loading={transitionMutation.isLoading}
                onClick={() =>
                  transitionMutation.mutate({ action: 'submit', comment })
                }
              >
                Submit for review
              </LoadingButton>
            )}
            {canActOnStage &&
              (() => {
                const st = (workflowStages || []).find((s) => s.stage_key === detail.status);
                return (
                  st?.email_actions ||
                  ['review', 'approval', 'vendor_approval'].includes(detail.status) ||
                  (st && !st.is_initial && !st.is_completed && !st.is_rejected)
                );
              })() && (
                <>
                  <LoadingButton
                    type="button"
                    className="btn btn-primary btn-sm"
                    icon={FiCheck}
                    loading={transitionMutation.isLoading}
                    onClick={() =>
                      transitionMutation.mutate({ action: 'approve', comment })
                    }
                  >
                    Approve
                    {(detail.pending_approvers || []).length > 0
                      ? ` (${(detail.stage_approvals || []).length}/${
                          (detail.stage_assignees || []).length || '?'
                        })`
                      : ''}
                  </LoadingButton>
                  <LoadingButton
                    type="button"
                    className="btn btn-danger btn-sm"
                    icon={FiX}
                    loading={transitionMutation.isLoading}
                    onClick={() =>
                      transitionMutation.mutate({ action: 'reject', comment })
                    }
                  >
                    Reject
                  </LoadingButton>
                  <LoadingButton
                    type="button"
                    className="btn btn-secondary btn-sm"
                    icon={FiArrowLeft}
                    loading={transitionMutation.isLoading}
                    onClick={() =>
                      transitionMutation.mutate({ action: 'send_back', comment })
                    }
                  >
                    Send back
                  </LoadingButton>
                </>
              )}
            {canManage && !isDraftStatus && !isRejectedStatus && !isAccounting && (
              <LoadingButton
                type="button"
                className="btn btn-danger btn-sm"
                icon={FiRotateCcw}
                loading={transitionMutation.isLoading}
                onClick={async () => {
                  const ok = await appConfirm(
                    `Revoke this ${kindLabel}? It will return to Draft and clear approvals.`
                  );
                  if (ok) transitionMutation.mutate({ action: 'revoke', comment });
                }}
              >
                Revoke
              </LoadingButton>
            )}
          </div>
        </div>
      )}
      {(detail.events || []).length > 0 && (
        <div className="req-section">
          <h3>History</h3>
          <ul className="req-events">
            {detail.events.map((ev) => (
              <li key={ev.id}>
                <strong>{ev.action}</strong> {ev.from_status || '—'} → {ev.to_status}
                {ev.actor_first_name ? ` · ${ev.actor_first_name} ${ev.actor_last_name}` : ''}
                <span className="svc-panel-meta">
                  {formatLocalDateTime(ev.createdon_timestamp)}
                  {ev.comment ? ` · ${ev.comment}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

const BuyingDocumentDetails = () => {
  const { documentId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const kind = kindFromPath(location.pathname);
  const isBill = kind === 'bill';
  const isQuote = kind === 'quotation';
  const isNew = !documentId || documentId === 'new';
  const supportsSourceFile = isBill || isQuote;

  const canManage = isBill
    ? hasPermission('erp_bills_manage')
    : isQuote
      ? hasPermission('erp_vendor_quote_manage')
      : hasPermission('erp_vendor_po_manage');
  const canApprove = hasPermission('erp_buying_approve');
  const canWorkflow = hasPermission('erp_buying_workflow_manage');
  const canView = isBill
    ? hasPermission('erp_bills_view') || canManage || canApprove
    : isQuote
      ? hasPermission('erp_vendor_quote_view') || canManage || canApprove
      : hasPermission('erp_vendor_po_view') || canManage || canApprove;

  const [form, setForm] = useState({
    vendor_id: '',
    vendor_po_id: '',
    lines: [emptyLine(), emptyLine()],
    tax: '',
    currency: 'USD',
    company_id: '',
    doc_date: '',
    due_date: '',
    notes: '',
    vendor_signatory_name: '',
    vendor_signatory_title: '',
    vendor_signatory_email: '',
    company_signatory_name: '',
    company_signatory_title: '',
  });
  const [sourceFile, setSourceFile] = useState(null);
  const [ocrExtracted, setOcrExtracted] = useState(null);
  const [comment, setComment] = useState('');
  const seededDetailId = useRef(null);
  const seededNewFromQuery = useRef(false);

  const titles = {
    bill: isNew ? 'New bill' : 'Bill',
    quotation: isNew ? 'New quotation' : 'Vendor quotation',
    po: isNew ? 'New Vendor PO' : 'Vendor PO',
  };
  usePageTitle(titles[kind]);

  const apiListPath = isBill ? '/buying/bills' : isQuote ? '/buying/quotations' : '/buying/purchase-orders';
  const listRoute = isBill ? '/buying?tab=bills' : isQuote ? '/buying?tab=quotations' : '/buying?tab=pos';
  const detailRouteBase = isBill ? '/buying/bills' : isQuote ? '/buying/quotations' : '/buying/purchase-orders';
  const listQueryKey = isBill
    ? ['buying', 'vendor-bills']
    : isQuote
      ? ['buying', 'vendor-quotations']
      : ['buying', 'vendor-pos'];
  const detailKey = isBill ? 'vendor-bill' : isQuote ? 'vendor-quotation' : 'vendor-po';

  const { data: vendors = [] } = useQuery(
    queryKeys.vendors,
    () => api.get('/vendors').then((r) => r.data),
    { enabled: canManage || Boolean(ocrExtracted) }
  );
  const canLinkPo = isBill || isQuote;
  const { data: vendorPos = [] } = useQuery(
    ['buying', 'vendor-pos', form.vendor_id],
    () =>
      api
        .get('/buying/purchase-orders', { params: { vendor_id: form.vendor_id, limit: 100 } })
        .then((r) => r.data),
    { enabled: canLinkPo && Boolean(form.vendor_id) && canManage }
  );
  const { data: companies = [] } = useQuery(
    queryKeys.accountingCompanies,
    () => api.get('/accounting/companies').then((r) => r.data),
    { enabled: canManage, staleTime: staleTimes.reference }
  );

  const { data: currencies = [] } = useQuery(
    'currencies-active',
    () => api.get('/currencies').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const { data: ocrStatus } = useQuery(
    queryKeys.buyingOcrStatus,
    () => api.get('/buying/ocr/status').then((r) => r.data),
    { enabled: supportsSourceFile && (isNew || canManage), staleTime: 30_000 }
  );

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useQuery(
    [detailKey, documentId],
    () => api.get(`${apiListPath}/${documentId}`).then((r) => r.data),
    { enabled: !isNew && Boolean(documentId) && canView, staleTime: 0 }
  );

  const { data: workflow } = useQuery(
    queryKeys.buyingWorkflow,
    () => api.get('/buying/workflow').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );
  const workflowStages = useMemo(() => workflow?.stages || [], [workflow?.stages]);
  const isPosted = Boolean(detail?.status === 'posted' || detail?.status === 'paid');
  const isDraftLike =
    isNew ||
    isBuyingInitialStatus(detail?.status, workflowStages) ||
    isBuyingRejectedStatus(detail?.status, workflowStages);
  const isCompletedWorkflow = isBuyingCompletedStatus(detail?.status, workflowStages);
  const canEdit = canManage && !isPosted && (isNew || isDraftLike);

  useEffect(() => {
    if (isNew) {
      seededDetailId.current = null;
      return;
    }
    if (!detail?.id || seededDetailId.current === detail.id) return;
    seededDetailId.current = detail.id;
    setForm({
      vendor_id: detail.vendor_id || '',
      vendor_po_id: detail.vendor_po_id || '',
      lines: linesFromApi(detail.lines),
      tax: centsToAmount(detail.tax_cents),
      currency: detail.currency || 'USD',
      company_id: detail.company_id || '',
      doc_date: detail.bill_date || detail.quotation_date || detail.order_date || '',
      due_date: detail.due_date || detail.valid_until || '',
      notes: detail.notes || '',
      vendor_signatory_name: detail.vendor_signatory_name || '',
      vendor_signatory_title: detail.vendor_signatory_title || '',
      vendor_signatory_email: detail.vendor_signatory_email || '',
      company_signatory_name: detail.company_signatory_name || '',
      company_signatory_title: detail.company_signatory_title || '',
    });
  }, [isNew, detail]);

  const applyExtracted = (extracted) => {
    if (!extracted) return;
    const matchedVendorId = extracted.vendor_id || matchVendorId(vendors, extracted.vendor_name);
    const noteBits = [extracted.notes, extracted.document_number];
    if (extracted.vendor_name && !matchedVendorId) noteBits.push(`Vendor on document: ${extracted.vendor_name}`);
    const ocrLines = linesFromOcr(extracted.lines);
    setForm((prev) => {
      const currency = extracted.currency || prev.currency;
      const vendor = vendors.find((v) => v.id === (matchedVendorId || prev.vendor_id));
      return {
        ...prev,
        vendor_id: matchedVendorId || prev.vendor_id,
        lines: ocrLines || prev.lines,
        tax: extracted.tax_cents ? centsToAmount(extracted.tax_cents) : prev.tax,
        currency,
        company_id:
          extracted.company_id ||
          resolveDocumentCompanyId(companies, {
            companyId: prev.company_id,
            vendor,
            countryCode: extracted.country_code,
            currency,
          }) ||
          prev.company_id,
        doc_date: extracted.date || prev.doc_date,
        due_date: extracted.due_date || prev.due_date,
        notes: noteBits.filter(Boolean).join('\n') || prev.notes,
      };
    });
    setOcrExtracted(extracted);
  };

  const scanMutation = useMutation(
    async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', isQuote ? 'quotation' : 'bill');
      return (await api.post('/buying/ocr/extract', fd)).data;
    },
    {
      onSuccess: (data) => {
        if (data?.extracted) {
          applyExtracted(data.extracted);
          if (data.mapped_by === 'ocr' && data.ai_error) {
            void appAlert(
              'OCR fields were applied. AI field mapping timed out or failed — review amounts and vendor, then save.'
            );
          }
        } else void appAlert(data?.message || 'OCR returned no fields. Enter details manually.');
      },
      onError: (e) =>
        void appAlert(e?.response?.data?.error || e.message || 'OCR is not available. Enter details manually.'),
    }
  );

  const uploadSource = async (id, file, extracted) => {
    if (!file || !id) return;
    const fd = new FormData();
    fd.append('file', file);
    if (extracted) {
      fd.append('ocr_extracted_json', JSON.stringify(extracted));
      fd.append('ocr_status', 'extracted');
    } else {
      fd.append('ocr_status', ocrStatus?.configured ? 'none' : 'skipped');
    }
    await api.post(`${apiListPath}/${id}/source-file`, fd);
  };

  const fallbackLineLabel = isBill ? 'Expense' : isQuote ? 'Quoted item' : 'Item';
  const lineTotals = useMemo(() => {
    const subtotalCents = (form.lines || []).reduce((sum, line) => {
      const qty = Number(line.quantity) || 0;
      const unit = Number(line.unit_price) || 0;
      return sum + Math.round(qty * unit * 100);
    }, 0);
    const taxCents = Math.round(Number(form.tax || 0) * 100);
    return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
  }, [form.lines, form.tax]);

  const applyPoEntitlements = async (poId, { append = false, vendorId } = {}) => {
    if (!poId) {
      setForm((prev) => ({ ...prev, vendor_po_id: '' }));
      return;
    }
    try {
      const po = (await api.get(`/buying/purchase-orders/${poId}`)).data;
      const incoming = linesFromApi(po.lines).filter(lineHasContent);
      setForm((prev) => {
        const kept = (prev.lines || []).filter(lineHasContent);
        let lines;
        if (append) lines = [...kept, ...incoming];
        else if (!kept.length) lines = incoming;
        else lines = kept;
        return {
          ...prev,
          vendor_id: po.vendor_id || vendorId || prev.vendor_id,
          vendor_po_id: poId,
          currency: po.currency || prev.currency,
          lines: lines.length ? lines : [emptyLine(), emptyLine()],
        };
      });
    } catch (e) {
      setForm((prev) => ({
        ...prev,
        vendor_id: vendorId || prev.vendor_id,
        vendor_po_id: poId,
      }));
      void appAlert(e?.response?.data?.error || e.message || 'Could not load vendor PO lines');
    }
  };

  useEffect(() => {
    if (!isNew) {
      seededNewFromQuery.current = false;
      return;
    }
    if (seededNewFromQuery.current) return;
    const vendorId = searchParams.get('vendor_id') || '';
    const poId = searchParams.get('vendor_po_id') || '';
    if (!vendorId && !poId) return;
    seededNewFromQuery.current = true;
    if (poId) void applyPoEntitlements(poId, { append: false, vendorId });
    else setForm((prev) => ({ ...prev, vendor_id: vendorId || prev.vendor_id }));
  }, [isNew, searchParams]);

  const buildBody = () => {
    const taxCents = lineTotals.taxCents;
    const totalCents = lineTotals.totalCents;
    const vendor = vendors.find((v) => v.id === form.vendor_id);
    const body = {
      vendor_id: form.vendor_id,
      currency: form.currency || 'USD',
      company_id:
        resolveDocumentCompanyId(companies, {
          companyId: form.company_id,
          vendor,
          currency: form.currency,
        }) || undefined,
      notes: form.notes || null,
      tax_cents: taxCents,
      total_cents: totalCents,
      lines: toPayloadLines(form.lines, fallbackLineLabel),
      vendor_signatory_name: form.vendor_signatory_name || null,
      vendor_signatory_title: form.vendor_signatory_title || null,
      vendor_signatory_email: form.vendor_signatory_email || null,
      company_signatory_name: form.company_signatory_name || null,
      company_signatory_title: form.company_signatory_title || null,
    };
    if (canLinkPo) body.vendor_po_id = form.vendor_po_id || null;
    if (isBill) {
      if (form.doc_date) body.bill_date = form.doc_date;
      if (form.due_date) body.due_date = form.due_date;
    } else if (isQuote) {
      if (form.doc_date) body.quotation_date = form.doc_date;
      if (form.due_date) body.valid_until = form.due_date;
    } else if (form.doc_date) {
      body.order_date = form.doc_date;
    }
    return body;
  };

  const createMutation = useMutation(
    async () => {
      const created = (await api.post(apiListPath, buildBody())).data;
      if (supportsSourceFile && sourceFile) {
        await uploadSource(created.id, sourceFile, ocrExtracted);
      }
      return created;
    },
    {
      onSuccess: (created) => {
        qc.invalidateQueries(listQueryKey);
        navigate(`${detailRouteBase}/${created.id}`, { replace: true });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const updateMutation = useMutation(
    async () => {
      const saved = (await api.put(`${apiListPath}/${documentId}`, buildBody())).data;
      if (supportsSourceFile && sourceFile) {
        await uploadSource(documentId, sourceFile, ocrExtracted);
      }
      return saved;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(listQueryKey);
        qc.invalidateQueries([detailKey, documentId]);
        setSourceFile(null);
        void appAlert('Saved.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const replaceFile = useMutation(
    async (file) => uploadSource(documentId, file, null),
    {
      onSuccess: () => {
        qc.invalidateQueries([detailKey, documentId]);
        qc.invalidateQueries(listQueryKey);
        setSourceFile(null);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const transitionMutation = useMutation(
    async ({ action, comment: cmt }) => {
      if (action === 'submit' && canEdit && documentId) {
        await api.put(`${apiListPath}/${documentId}`, buildBody());
        if (supportsSourceFile && sourceFile) {
          await uploadSource(documentId, sourceFile, ocrExtracted);
        }
      }
      return (await api.post(`${apiListPath}/${documentId}/transition`, { action, comment: cmt })).data;
    },
    {
      onSuccess: (row) => {
        qc.invalidateQueries([detailKey, documentId]);
        qc.invalidateQueries(listQueryKey);
        setComment('');
        if (row?.status === detail?.status && (row?.pending_approvers || []).length) {
          void appAlert(
            `Recorded your approval. Waiting on ${row.pending_approvers.length} more assignee(s).`
          );
        }
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const postBill = useMutation(
    async () => (await api.post(`/buying/bills/${documentId}/post`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(['buying', 'vendor-bills']);
        qc.invalidateQueries(['vendor-bill', documentId]);
        void appAlert('Bill posted to GL.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const unpostBill = useMutation(
    async () => (await api.post(`/buying/bills/${documentId}/unpost`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(['buying', 'vendor-bills']);
        qc.invalidateQueries(['vendor-bill', documentId]);
        qc.invalidateQueries(queryKeys.accountingJournals);
        void appAlert(detail?.approved_at ? 'Bill reverted to approved.' : 'Bill reverted to draft.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const back = () => navigate(listRoute);
  const Icon = isBill || isQuote ? FiFileText : FiShoppingBag;
  const sourcePath = `${apiListPath}/${documentId}/source-file`;
  const canActOnStage = useMemo(() => {
    if (!canApprove && !canWorkflow) return false;
    if (canWorkflow) return true;
    const assignees = detail?.stage_assignees || [];
    if (!assignees.length) return true;
    const uid = Number(user?.id);
    const isMe = (a) => Number(a.id || a.user_id) === uid;
    return assignees.some(isMe) || (detail?.pending_approvers || []).some(isMe);
  }, [canApprove, canWorkflow, detail, user?.id]);
  const showReadonly = !isNew && detail && !canEdit;
  const workflowPanel = !isNew && detail ? (
    <BuyingWorkflowPanel
      detail={detail}
      kind={kind}
      workflowStages={workflowStages}
      canManage={canManage}
      canActOnStage={canActOnStage}
      comment={comment}
      setComment={setComment}
      transitionMutation={transitionMutation}
    />
  ) : null;
  const docNumber = detail
    ? isBill
      ? detail.bill_number
      : isQuote
        ? detail.quotation_number
        : detail.po_number
    : '';
  const dateLabel = isBill ? 'Bill date' : isQuote ? 'Quotation date' : 'Order date';
  const dueLabel = isBill ? 'Due date' : isQuote ? 'Valid until' : null;

  if (!canView && !isNew) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="error">You do not have permission to view this document.</div>
      </div>
    );
  }

  if (!isNew && isLoading) return <div className="loading">Loading…</div>;

  if (!isNew && (isError || !detail)) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">
            {error?.response?.data?.error || error?.message || 'Not found'}
          </div>
        </div>
      </div>
    );
  }

  if (showReadonly) {
    const number = docNumber;
    const canReplaceFile = false;
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="svc-package-page-header">
            <h1>
              <Icon aria-hidden /> {number}
            </h1>
            <p className="page-subtitle">
              {isBill ? 'Vendor bill' : isQuote ? 'Vendor quotation' : 'Vendor purchase order'}
              {detail.vendor_name ? ` · ${detail.vendor_name}` : ''}
            </p>
          </div>
          {workflowPanel}
          <div className="erp-form-grid">
            <div className="erp-field">
              <label>Status</label>
              <div className="erp-readonly-value"><StatusBadge status={detail.status} /></div>
            </div>
            <div className="erp-field">
              <label>Currency</label>
              <span className="erp-readonly-value erp-code">{detail.currency || 'USD'}</span>
            </div>
            <div className="erp-field">
              <label>Legal company</label>
              <span className="erp-readonly-value">
                {detail.company_name
                  ? `${detail.company_name}${detail.company_country ? ` · ${detail.company_country}` : ''}${detail.company_currency ? ` · ${detail.company_currency}` : ''}`
                  : '—'}
              </span>
            </div>
            <div className="erp-field">
              <label>Total</label>
              <span className="erp-readonly-value erp-num">{money(detail.total_cents, detail.currency)}</span>
            </div>
            {detail.vendor_po_number || detail.vendor_po_id ? (
              <div className="erp-field">
                <label>Vendor PO</label>
                <span className="erp-readonly-value">
                  <Link to={`/buying/purchase-orders/${detail.vendor_po_id}`}>
                    <span className="erp-code">{detail.vendor_po_number || detail.vendor_po_id}</span>
                  </Link>
                </span>
              </div>
            ) : null}
            <div className="erp-field">
              <label>Date</label>
              <span className="erp-readonly-value">
                {detail.bill_date || detail.quotation_date || detail.order_date || '—'}
              </span>
            </div>
            {supportsSourceFile ? (
              <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
                <label>Vendor copy</label>
                {detail.source_file_r2_key ? (
                  <div className="erp-attachment">
                    <FiPaperclip aria-hidden />
                    <span className="erp-attachment-name">{detail.source_file_name || 'Attached file'}</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => openAuthenticatedFile(sourcePath)}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        downloadAuthenticatedFile(sourcePath, detail.source_file_name || `${number}.pdf`)
                      }
                    >
                      <FiDownload aria-hidden /> Download
                    </button>
                  </div>
                ) : (
                  <span className="erp-readonly-value">No copy uploaded</span>
                )}
                {canReplaceFile ? (
                  <div className="erp-file-row" style={{ marginTop: '0.6rem' }}>
                    <input
                      type="file"
                      accept={ACCEPT}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) replaceFile.mutate(file);
                      }}
                    />
                    {replaceFile.isLoading ? <span>Uploading…</span> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {(detail.lines || []).length > 0 ? (
            <>
              <div className="svc-form-section-head">
                <h3>Line items</h3>
              </div>
              <div className="svc-sales-lines">
                {detail.lines.map((line) => (
                  <div key={line.id || `${line.line_order}-${line.description}`} className="svc-sales-line-row svc-sales-line-row--buying">
                    <span className="erp-readonly-value">{line.description || '—'}</span>
                    <span className="erp-readonly-value erp-num">{line.quantity ?? 1}</span>
                    <span className="erp-readonly-value erp-num">{money(line.unit_price_cents, detail.currency)}</span>
                    <span className="svc-sales-line-amount">{money(line.amount_cents, detail.currency)}</span>
                  </div>
                ))}
              </div>
              <div className="svc-sales-totals">
                <span>Subtotal {money(detail.subtotal_cents, detail.currency)}</span>
                <span>Tax {money(detail.tax_cents, detail.currency)}</span>
                <strong>Total {money(detail.total_cents, detail.currency)}</strong>
              </div>
            </>
          ) : null}
          {!isBill && !isQuote ? (
            <LinkedVendorPoDocuments
              detail={detail}
              canQuote={hasPermission('erp_vendor_quote_manage')}
              canBill={hasPermission('erp_bills_manage')}
            />
          ) : null}
          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={back}>
              Back
            </button>
            {isBill && isCompletedWorkflow && canManage && hasPermission('erp_journals_post') ? (
              <LoadingButton
                type="button"
                className="btn btn-primary"
                loading={postBill.isLoading}
                onClick={() => postBill.mutate()}
              >
                Post to GL
              </LoadingButton>
            ) : null}
            {isBill &&
            detail.status === 'posted' &&
            Number(detail.amount_paid_cents || 0) === 0 &&
            canManage &&
            hasPermission('erp_journals_post') ? (
              <LoadingButton
                type="button"
                className="btn btn-secondary"
                icon={FiRotateCcw}
                loading={unpostBill.isLoading}
                onClick={async () => {
                  const ok = await appConfirm(
                    detail.approved_at
                      ? 'Unpost this bill? Its GL entry will leave the ledger and the bill returns to Approved.'
                      : 'Revert this bill to draft? Its GL entry will leave the ledger so you can correct it and post again.'
                  );
                  if (ok) unpostBill.mutate();
                }}
              >
                {detail.approved_at ? 'Unpost' : 'Revert to draft'}
              </LoadingButton>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">You do not have permission to create this document.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
      </div>
      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>
            <Icon aria-hidden />{' '}
            {isNew
              ? isBill
                ? 'New bill'
                : isQuote
                  ? 'New quotation'
                  : 'New Vendor PO'
              : docNumber || (isBill ? 'Bill' : isQuote ? 'Vendor quotation' : 'Vendor PO')}
          </h1>
          <p className="page-subtitle">
            {isNew
              ? isBill
                ? 'Record supplier expense and payable. Attach the vendor copy; posting writes the ledger entry.'
                : isQuote
                  ? 'Capture a supplier quotation and store the original copy in R2.'
                  : 'Commit spend against a supplier before the bill arrives.'
              : `${isBill ? 'Vendor bill' : isQuote ? 'Vendor quotation' : 'Vendor purchase order'}${
                  detail?.vendor_name ? ` · ${detail.vendor_name}` : ''
                }`}
          </p>
        </div>
        {workflowPanel}
        {supportsSourceFile && isNew ? (
          <p className="erp-ocr-banner">
            {ocrStatus?.configured ? (
              <>
                <strong>OCR is on.</strong> Choose a PDF or image — it is sent to OCR immediately,
                then AI fills the form fields from the app catalog. Review before saving.
              </>
            ) : (
              <>
                <strong>OCR is not configured.</strong> Enter vendor, dates, and amounts manually,
                then upload the bill or quotation copy. Configure an OCR API later under Settings →
                OCR.
              </>
            )}
          </p>
        ) : null}
        <form
          className="svc-package-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (isNew) createMutation.mutate();
            else updateMutation.mutate();
          }}
        >
          <div className="erp-form-grid">
            <div className="erp-field">
              <label htmlFor="bd-vendor">Vendor</label>
              <select
                id="bd-vendor"
                required
                value={form.vendor_id}
                onChange={(e) => {
                  const vendorId = e.target.value;
                  const vendor = vendors.find((v) => v.id === vendorId);
                  setForm({
                    ...form,
                    vendor_id: vendorId,
                    vendor_po_id: '',
                    currency: vendor?.currency || form.currency,
                    company_id:
                      resolveDocumentCompanyId(companies, {
                        vendor,
                        currency: vendor?.currency || form.currency,
                      }) || form.company_id,
                  });
                }}
              >
                <option value="">Select…</option>
                {(vendors || []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                    {v.currency ? ` · ${v.currency}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field form-group--currency">
              <label htmlFor="bd-currency">Currency</label>
              <select
                id="bd-currency"
                value={form.currency || 'USD'}
                onChange={(e) => {
                  const currency = e.target.value;
                  const vendor = vendors.find((v) => v.id === form.vendor_id);
                  setForm({
                    ...form,
                    currency,
                    company_id:
                      resolveDocumentCompanyId(companies, {
                        companyId: vendor?.company_id,
                        vendor,
                        currency,
                      }) || form.company_id,
                  });
                }}
              >
                {(currencies || []).map((c) => {
                  const code = c.code || c.currency_code || c;
                  return (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  );
                })}
                {(!currencies || currencies.length === 0) &&
                  ['USD', 'INR', 'CAD', 'EUR', 'GBP'].map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
              </select>
            </div>
            <div className="erp-field">
              <label htmlFor="bd-company">Legal company</label>
              <select
                id="bd-company"
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              >
                <option value="">Match country + currency</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {companyOptionLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field">
              <label htmlFor="bd-date">{dateLabel}</label>
              <input
                id="bd-date"
                type="date"
                value={form.doc_date}
                onChange={(e) => setForm({ ...form, doc_date: e.target.value })}
              />
            </div>
            {dueLabel ? (
              <div className="erp-field">
                <label htmlFor="bd-due">{dueLabel}</label>
                <input
                  id="bd-due"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
              </div>
            ) : null}
            {canLinkPo ? (
              <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="bd-po">Vendor PO</label>
                <div className="erp-file-row">
                  <select
                    id="bd-po"
                    value={form.vendor_po_id}
                    disabled={!form.vendor_id}
                    onChange={(e) => {
                      const poId = e.target.value;
                      if (!poId) setForm({ ...form, vendor_po_id: '' });
                      else void applyPoEntitlements(poId, { append: false });
                    }}
                  >
                    <option value="">{form.vendor_id ? 'None — enter lines only' : 'Select a vendor first'}</option>
                    {(vendorPos || []).map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.po_number}
                        {po.total_cents != null ? ` · ${money(po.total_cents, po.currency || form.currency)}` : ''}
                        {po.status ? ` · ${po.status}` : ''}
                      </option>
                    ))}
                  </select>
                  {form.vendor_po_id ? (
                    <>
                      <Link
                        className="btn btn-secondary btn-sm"
                        to={`/buying/purchase-orders/${form.vendor_po_id}`}
                      >
                        Open PO
                      </Link>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void applyPoEntitlements(form.vendor_po_id, { append: true })}
                      >
                        Add PO lines
                      </button>
                    </>
                  ) : null}
                </div>
                <p className="form-hint">
                  Connect this {isBill ? 'bill' : 'quotation'} to a Vendor PO. Selecting a PO copies
                  its line items when this form has none yet.
                </p>
              </div>
            ) : null}
          </div>

          <div className="svc-form-section-head">
            <h3>Line items</h3>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
            >
              <FiPlus /> Add line
            </button>
          </div>
          <div className="svc-sales-lines">
            {(form.lines || []).map((line, idx) => (
              <div key={idx} className="svc-sales-line-row svc-sales-line-row--buying">
                <input
                  placeholder={fallbackLineLabel}
                  aria-label={`Line ${idx + 1} description`}
                  value={line.description}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], description: e.target.value };
                    setForm({ ...form, lines });
                  }}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Qty"
                  aria-label={`Line ${idx + 1} quantity`}
                  value={line.quantity}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], quantity: e.target.value };
                    setForm({ ...form, lines });
                  }}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Unit price"
                  aria-label={`Line ${idx + 1} unit price`}
                  value={line.unit_price}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], unit_price: e.target.value };
                    setForm({ ...form, lines });
                  }}
                />
                <span className="svc-sales-line-amount">
                  {money(
                    Math.round((Number(line.quantity) || 0) * (Number(line.unit_price) || 0) * 100),
                    form.currency
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  aria-label={`Remove line ${idx + 1}`}
                  disabled={(form.lines || []).length <= 1}
                  onClick={() =>
                    setForm({
                      ...form,
                      lines: form.lines.filter((_, i) => i !== idx),
                    })
                  }
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>
          <div className="svc-sales-totals">
            <span>Subtotal {money(lineTotals.subtotalCents, form.currency)}</span>
            <label className="erp-tax-inline">
              Tax
              <input
                id="bd-tax"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={form.tax}
                onChange={(e) => setForm({ ...form, tax: e.target.value })}
              />
            </label>
            <strong>Total {money(lineTotals.totalCents, form.currency)}</strong>
          </div>

          {!isNew && !isBill && !isQuote ? (
            <LinkedVendorPoDocuments
              detail={detail}
              canQuote={hasPermission('erp_vendor_quote_manage')}
              canBill={hasPermission('erp_bills_manage')}
            />
          ) : null}

          <div className="erp-form-grid">
            {supportsSourceFile ? (
              <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="bd-file">Vendor copy (PDF or image)</label>
                {!isNew && detail?.source_file_r2_key ? (
                  <div className="erp-attachment" style={{ marginBottom: '0.6rem' }}>
                    <FiPaperclip aria-hidden />
                    <span className="erp-attachment-name">{detail.source_file_name || 'Attached file'}</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => openAuthenticatedFile(sourcePath)}
                    >
                      Open
                    </button>
                  </div>
                ) : null}
                <div className="erp-file-row">
                  <input
                    id="bd-file"
                    type="file"
                    accept={ACCEPT}
                    disabled={scanMutation.isLoading}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setSourceFile(file);
                      setOcrExtracted(null);
                      if (file && ocrStatus?.configured) scanMutation.mutate(file);
                    }}
                  />
                  {ocrStatus?.configured ? (
                    <LoadingButton
                      type="button"
                      className="btn btn-secondary"
                      icon={FiCamera}
                      loading={scanMutation.isLoading}
                      disabled={!sourceFile}
                      onClick={() => sourceFile && scanMutation.mutate(sourceFile)}
                    >
                      {ocrExtracted ? 'Scan again' : 'Scan with OCR'}
                    </LoadingButton>
                  ) : null}
                </div>
                {scanMutation.isLoading ? (
                  <p className="form-hint">Sending document to OCR, then mapping fields with AI…</p>
                ) : ocrExtracted ? (
                  <p className="form-hint">OCR filled vendor, dates, and line items. Review before saving.</p>
                ) : null}
              </div>
            ) : null}
            <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="bd-notes">Notes</label>
              <textarea
                id="bd-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="bd-vsig-name">Vendor signatory</label>
              <input
                id="bd-vsig-name"
                value={form.vendor_signatory_name}
                onChange={(e) => setForm({ ...form, vendor_signatory_name: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="bd-vsig-title">Vendor title</label>
              <input
                id="bd-vsig-title"
                value={form.vendor_signatory_title}
                onChange={(e) => setForm({ ...form, vendor_signatory_title: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="bd-vsig-email">Vendor signatory email</label>
              <input
                id="bd-vsig-email"
                type="email"
                value={form.vendor_signatory_email}
                onChange={(e) => setForm({ ...form, vendor_signatory_email: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="bd-csig-name">Company signatory</label>
              <input
                id="bd-csig-name"
                value={form.company_signatory_name}
                onChange={(e) => setForm({ ...form, company_signatory_name: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="bd-csig-title">Company title</label>
              <input
                id="bd-csig-title"
                value={form.company_signatory_title}
                onChange={(e) => setForm({ ...form, company_signatory_title: e.target.value })}
              />
            </div>
          </div>
          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={back}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={createMutation.isLoading || updateMutation.isLoading || scanMutation.isLoading}
              disabled={!form.vendor_id || scanMutation.isLoading}
            >
              {isNew ? 'Create' : 'Save changes'}
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuyingDocumentDetails;
